import { motion } from 'framer-motion';
import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { formatDeliveryDate, getEstimatedDeliveryDate } from '../utils/orderUtils';

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const { order, message } = location.state || {};

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-xl shadow-lg p-6 sm:p-8"
        >
          {/* Success Header */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <svg className="w-10 h-10 text-[#008000]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </motion.div>

            <h1 className="text-3xl font-bold text-gray-900 mb-2">Order Confirmed!</h1>
            <p className="text-lg text-gray-600">
              {message || 'Thank you for your order. We\'ll start working on it right away.'}
            </p>
          </div>

          {/* Order Details */}
          {order && (
            <div className="space-y-6">
              {/* Order Info */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Order Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Order Number</p>
                    <p className="text-lg font-mono text-gray-900">{order.orderNumber || orderId}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Order Date</p>
                    <p className="text-lg text-gray-900">
                      {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : new Date().toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Total Amount</p>
                    <p className="text-lg font-semibold text-gray-900">₹{order.total || order.totalAmount}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Payment Status</p>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-[#008000]">
                      Paid
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Estimated Delivery</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatDeliveryDate(getEstimatedDeliveryDate(order))}
                    </p>
                  </div>
                </div>
              </div>

              {/* Items */}
              {order.items && order.items.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Items Ordered</h2>
                  <div className="space-y-4">
                    {order.items.map((item, index) => (
                      <div key={index} className="flex items-start space-x-4 bg-white p-4 rounded-lg">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">
                            {item.product?.name || 'Business Cards'}
                          </h3>
                          <div className="mt-1 text-sm text-gray-500 space-y-1">
                            <p>Quantity: {item.quantity} </p>
                            {item.customizations && (
                              <>
                                {item.customizations.finish && (
                                  <p>Finish: {item.customizations.finish}</p>
                                )}
                                {item.customizations.corners && (
                                  <p>Corners: {item.customizations.corners}</p>
                                )}
                                {item.customizations.paperType && (
                                  <p>Paper: {item.customizations.paperType}</p>
                                )}
                              </>
                            )}
                            {item.designUpload && (
                              <p className="text-[#008000]">
                                Design: {item.designUpload.type === 'upload' ? 'Custom Upload' : 'Template Selected'}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-gray-900">₹{item.totalPrice}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Shipping Address */}
              {order.shippingAddress && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Shipping Address</h2>
                  <div className="text-gray-700">
                    {order.deliveryType === 'self-pickup' || order.shippingAddress.addressLine1 === 'Pick from store' ? (
                      <p className="font-medium">Pick from store</p>
                    ) : (
                      <>
                        <p className="font-medium">{order.shippingAddress.fullName}</p>
                        <p>{order.shippingAddress.street}</p>
                        <p>{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.postalCode}</p>
                        <p>{order.shippingAddress.country}</p>
                        {order.shippingAddress.phone && (
                          <p className="mt-2">Phone: {order.shippingAddress.phone}</p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Next Steps */}
          <div className="mt-8 bg-blue-50 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-blue-900 mb-4">What's Next?</h2>
            <div className="space-y-3 text-blue-800">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-200 rounded-full flex items-center justify-center mt-0.5">
                  <span className="text-xs font-bold">1</span>
                </div>
                <p>We'll review your order and design within 2-4 hours</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-200 rounded-full flex items-center justify-center mt-0.5">
                  <span className="text-xs font-bold">2</span>
                </div>
                <p>Your order will be printed and quality checked</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-200 rounded-full flex items-center justify-center mt-0.5">
                  <span className="text-xs font-bold">3</span>
                </div>
                <p>We'll ship your order with tracking information</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-200 rounded-full flex items-center justify-center mt-0.5">
                  <span className="text-xs font-bold">4</span>
                </div>
                <p>Delivery within 1-2 business days for standard orders</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-[#008000] hover:bg-[#008000] transition-colors"
            >
              Continue Shopping
            </Link>

            <Link
              to="/orders"
              className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              View All Orders
            </Link>
          </div>

          {/* Contact Support */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              Questions about your order?
              <a href="mailto:support@xpressprint.com" className="text-[#008000] hover:text-[#008000] ml-1">
                Contact Support
              </a>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
